import { ClosestVenue, findClosestVenue } from '../Utils/geoFenceUtils'
import { venueData } from '../Utils/venueData'
import { trackOptimoveEvent } from '@core/Services/Optimove/optimove'
import { GeoLocation } from './venueModeService'

let currentClosestVenue: ClosestVenue | null = null
let venueEntryTime: number | null = null
let currentGeoLocation: GeoLocation | null = null
let venueTrackingInterval: NodeJS.Timeout | null = null

const VENUE_TRACKING_INTERVAL = 60 * 1000

type BaseEventData = {
  longitude: number
  latitude: number
  accuracy: number
}

const createBaseEventData = (geoLocation: GeoLocation): BaseEventData => {
  return {
    longitude: geoLocation.longitude || 0,
    latitude: geoLocation.latitude || 0,
    accuracy: geoLocation.accuracy || 0,
  }
}

const trackVenueChange = (geoLocation: GeoLocation): void => {
  const geoLocationWithISOTimestamp = {
    ...geoLocation,
    timestamp: new Date(geoLocation.timestamp).toISOString(),
  }

  const closestVenue = findClosestVenue(geoLocationWithISOTimestamp, venueData)
  const newLocationId = closestVenue.locationId
  const currentTime = geoLocation.timestamp

  const baseEventData = createBaseEventData(geoLocation)

  const hasVenueChanged = currentClosestVenue?.locationId !== newLocationId || currentClosestVenue?.inside !== closestVenue.inside;
  let visitDurationSeconds = 0

  if (hasVenueChanged) {
    if (currentClosestVenue?.inside && venueEntryTime) {
      visitDurationSeconds = Math.round((currentTime - venueEntryTime) / 1000) // Note: Fixed duration calculation to seconds
      trackOptimoveEvent({
        eventName: 'venue_mode_exit',
        data: {
          ...baseEventData,
          location_id: currentClosestVenue.locationId,
          venue_id: currentClosestVenue.venueId,
          venue_name: currentClosestVenue.name,
          visit_duration_seconds: visitDurationSeconds,
          exit_timestamp: new Date(currentTime).toISOString(),
        },
      })
    }

    // Update current venue tracking
    currentClosestVenue = closestVenue
    venueEntryTime = currentTime

    if (closestVenue.inside) {
      visitDurationSeconds = 0
      trackOptimoveEvent({
        eventName: 'venue_mode_entry',
        data: {
          ...baseEventData,
          venue_id: closestVenue.venueId,
          venue_name: closestVenue.name,
          location_id: closestVenue.locationId,
          entry_timestamp: new Date(currentTime).toISOString(),
        },
      })
    }
  } else {
    // Same venue - calculate current visit duration
    if (venueEntryTime) {
      visitDurationSeconds = Math.round((currentTime - venueEntryTime) / 1000) // Note: Fixed duration calculation to seconds
    }

    if (closestVenue.inside) {
      trackOptimoveEvent({
        eventName: 'venue_mode_present',
        data: {
          ...baseEventData,
          venue_id: closestVenue.venueId,
          venue_name: closestVenue.name,
          location_id: closestVenue.locationId,
          visit_duration_seconds: visitDurationSeconds,
        },
      })
    } else {
      if (currentClosestVenue?.inside && visitDurationSeconds) {
        trackOptimoveEvent({
          eventName: 'venue_mode_exit',
          data: {
            ...baseEventData,
            location_id: closestVenue.locationId,
            venue_id: closestVenue.venueId,
            venue_name: closestVenue.name,
            visit_duration_seconds: visitDurationSeconds,
            exit_timestamp: new Date(currentTime).toISOString(),
          },
        })
      
        venueEntryTime = null
        visitDurationSeconds = 0
      }
    }
  }
}

export const updateVenueLocation = (geoLocation: GeoLocation): void => {
  currentGeoLocation = geoLocation
  trackVenueChange(geoLocation)

  if (venueTrackingInterval) {
    clearInterval(venueTrackingInterval)
    venueTrackingInterval = setInterval(() => {
      runVenueTracking()
    }, VENUE_TRACKING_INTERVAL)
  }
}

export const runVenueTracking = (): void => {
  if (currentGeoLocation) {
    trackVenueChange({
      ...currentGeoLocation,
      timestamp: Date.now(),
    })
  }
}

export const startVenueTracking = (): void => {
  if (venueTrackingInterval) {
    return
  }

  runVenueTracking()

  if (!venueTrackingInterval) {
    venueTrackingInterval = setInterval(() => {
      runVenueTracking()
    }, VENUE_TRACKING_INTERVAL)
  }
}

export const stopVenueTracking = (): void => {
  if (venueTrackingInterval) {
    clearInterval(venueTrackingInterval)
    venueTrackingInterval = null
  }
}

export const reset = (): void => {
  stopVenueTracking()
  currentClosestVenue = null
  venueEntryTime = null
  currentGeoLocation = null
}